import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { types as sdkTypes, createImageVariantConfig } from '../../util/sdkLoader';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { createSlug } from '../../util/urlHelpers';

const { UUID } = sdkTypes;

// ================ Action types ================ //

export const SET_INITIAL_STATE = 'app/ShopStorefrontPage/SET_INITIAL_STATE';

export const QUERY_SHOP_REQUEST = 'app/ShopStorefrontPage/QUERY_SHOP_REQUEST';
export const QUERY_SHOP_SUCCESS = 'app/ShopStorefrontPage/QUERY_SHOP_SUCCESS';
export const QUERY_SHOP_ERROR = 'app/ShopStorefrontPage/QUERY_SHOP_ERROR';

export const QUERY_LISTINGS_REQUEST = 'app/ShopStorefrontPage/QUERY_LISTINGS_REQUEST';
export const QUERY_LISTINGS_SUCCESS = 'app/ShopStorefrontPage/QUERY_LISTINGS_SUCCESS';
export const QUERY_LISTINGS_ERROR = 'app/ShopStorefrontPage/QUERY_LISTINGS_ERROR';

// ================ Reducer ================ //

const initialState = {
    shopUserId: null,
    shopUser: null,
    shopShowError: null,
    listingRefs: [],
    pagination: null,
    queryListingsError: null,
    queryListingsInProgress: false,
};

export default function shopStorefrontPageReducer(state = initialState, action = {}) {
    const { type, payload } = action;
    switch (type) {
        case SET_INITIAL_STATE:
            return { ...initialState };

        case QUERY_SHOP_REQUEST:
            return { ...state, shopShowError: null, shopUserId: null, shopUser: null };
        case QUERY_SHOP_SUCCESS:
            return { ...state, shopUserId: payload.userId, shopUser: payload.user };
        case QUERY_SHOP_ERROR:
            return { ...state, shopShowError: payload };

        case QUERY_LISTINGS_REQUEST:
            return {
                ...state,
                listingRefs: [],
                queryListingsError: null,
                queryListingsInProgress: true,
            };
        case QUERY_LISTINGS_SUCCESS:
            return {
                ...state,
                listingRefs: payload.listingRefs,
                pagination: payload.pagination,
                queryListingsInProgress: false,
            };
        case QUERY_LISTINGS_ERROR:
            return {
                ...state,
                listingRefs: [],
                queryListingsError: payload,
                queryListingsInProgress: false,
            };

        default:
            return state;
    }
}

// ================ Action creators ================ //

export const setInitialState = () => ({
    type: SET_INITIAL_STATE,
});

export const queryShopRequest = () => ({
    type: QUERY_SHOP_REQUEST,
});

export const queryShopSuccess = (userId, user) => ({
    type: QUERY_SHOP_SUCCESS,
    payload: { userId, user },
});

export const queryShopError = e => ({
    type: QUERY_SHOP_ERROR,
    error: true,
    payload: e,
});

export const queryListingsRequest = () => ({
    type: QUERY_LISTINGS_REQUEST,
});

export const queryListingsSuccess = (listingRefs, pagination) => ({
    type: QUERY_LISTINGS_SUCCESS,
    payload: { listingRefs, pagination },
});

export const queryListingsError = e => ({
    type: QUERY_LISTINGS_ERROR,
    error: true,
    payload: e,
});

// ================ Thunks ================ //

/**
 * Find a shop user by their display name slug using listing query
 * Since SDK doesn't support sdk.users.query(), we query listings with author included
 * and find the author whose displayName matches the slug.
 */
export const queryShopBySlug = (slug, config) => (dispatch, getState, sdk) => {
    dispatch(queryShopRequest());

    const {
        aspectWidth = 1,
        aspectHeight = 1,
        variantPrefix = 'listing-card',
    } = config?.layout?.listingImage || {};
    const aspectRatio = aspectHeight / aspectWidth;

    // Query listings with author included to find matching shop
    return sdk.listings
        .query({
            include: ['author', 'author.profileImage', 'images'],
            'fields.user': ['profile.displayName', 'profile.abbreviatedName', 'profile.bio', 'profile.publicData'],
            'fields.image': [
                'variants.square-small',
                'variants.square-small2x',
                `variants.${variantPrefix}`,
                `variants.${variantPrefix}-2x`,
            ],
            ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
            ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
            'limit.images': 1,
            perPage: 100,
        })
        .then(response => {
            const entities = denormalisedResponseEntities(response);

            // Get unique authors from listings
            const authorsMap = new Map();
            entities.forEach(listing => {
                const author = listing.author;
                if (author && author.id) {
                    authorsMap.set(author.id.uuid, author);
                }
            });

            // Find author whose displayName matches the slug
            let matchedAuthor = null;
            for (const author of authorsMap.values()) {
                const displayName = author.attributes?.profile?.displayName || '';
                const authorSlug = createSlug(displayName);
                if (authorSlug === slug) {
                    matchedAuthor = author;
                    break;
                }
            }

            if (matchedAuthor) {
                const userFields = config?.user?.userFields;
                const sanitizeConfig = { userFields };
                dispatch(addMarketplaceEntities(response, sanitizeConfig));
                dispatch(queryShopSuccess(matchedAuthor.id, matchedAuthor));
                return matchedAuthor;
            } else {
                const error = new Error('Shop not found');
                error.status = 404;
                dispatch(queryShopError(storableError(error)));
                return null;
            }
        })
        .catch(e => {
            dispatch(queryShopError(storableError(e)));
            return null;
        });
};

/**
 * Query all listings for a specific shop user (no category filter - filtering done client-side)
 */
export const queryShopListings = (userId, config) => (dispatch, getState, sdk) => {
    dispatch(queryListingsRequest());

    const {
        aspectWidth = 1,
        aspectHeight = 1,
        variantPrefix = 'listing-card',
    } = config?.layout?.listingImage || {};
    const aspectRatio = aspectHeight / aspectWidth;

    return sdk.listings
        .query({
            author_id: userId,
            include: ['author', 'author.profileImage', 'images'],
            'fields.listing': [
                'title',
                'geolocation',
                'price',
                'deleted',
                'state',
                'publicData.listingType',
                'publicData.transactionProcessAlias',
                'publicData.unitType',
                'publicData.location',
                'publicData.categoryLevel1',
            ],
            'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
            'fields.image': [
                'variants.scaled-small',
                'variants.scaled-medium',
                `variants.${variantPrefix}`,
                `variants.${variantPrefix}-2x`,
            ],
            ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
            ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
            'limit.images': 1,
        })
        .then(response => {
            const listingFields = config?.listing?.listingFields;
            const sanitizeConfig = { listingFields };

            dispatch(addMarketplaceEntities(response, sanitizeConfig));

            const listings = response.data.data;
            const listingRefs = listings
                .filter(l => !l.attributes.deleted && l.attributes.state === 'published')
                .map(({ id, type }) => ({ id, type }));

            dispatch(queryListingsSuccess(listingRefs, response.data.meta));
            return response;
        })
        .catch(e => {
            dispatch(queryListingsError(storableError(e)));
            return null;
        });
};

/**
 * Load data for the ShopStorefrontPage
 */
export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
    const { slug } = params || {};

    // Clear state
    dispatch(setInitialState());

    if (!slug) {
        const error = new Error('No shop slug provided');
        error.status = 404;
        dispatch(queryShopError(storableError(error)));
        return Promise.resolve(null);
    }

    return dispatch(queryShopBySlug(slug, config)).then(shopUser => {
        if (shopUser) {
            // Fetch all listings - category filtering is done client-side
            return dispatch(queryShopListings(shopUser.id, config));
        }
        return null;
    });
};
